import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import MessageItem from "./MessageItem";
import { getChatMessagesList, updateLastSeenChat } from "../firestore";
import { useParams } from "react-router-dom";
import { Chat, EntityInfo, Message, UserInfo } from "../../../types";
import { useProfileData } from "../../../hooks/useProfileData";

type MessageListProps = {
  sendToUser: UserInfo;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  chats: Chat[];
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
};

const MessageList = ({
  sendToUser,
  setIsActive,
  chats,
  scrollContainerRef,
}: MessageListProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const { chatId } = useParams<{ chatId: string }>();

  const { data } = useProfileData();
  const userId = data.id;

  const hasScrolled = useRef(false);

  useEffect(() => {
    hasScrolled.current = false;
  }, [chatId]);

  useEffect(() => {
    if (!hasScrolled.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      hasScrolled.current = true;
    }
  }, [scrollContainerRef, messages]);

  useEffect(() => {
    if (!chatId || !userId) return;
    updateLastSeenChat(chatId, userId);
  }, []);

  useEffect(() => {
    if (!chatId) return;

    const getChatMessages = async () => {
      try {
        const chatMessages = await getChatMessagesList(chatId);
        setMessages(chatMessages as Message[]);
      } catch (error) {
        // console.error("Error fetching chat messages:", error);
        setMessages([]);
      }
    };

    getChatMessages();
  }, [chatId, chats]);

  if (messages.length === 0)
    return (
      <Box
        sx={{
          flex: 1,
          padding: "48px 25px 12px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "16px",
        }}
      >
        <Typography variant="body16Bold">There're no messages</Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: "1px solid #EAECF0",
        marginBottom: "16px",
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          height: "100%",
          maxHeight: "620px",
          display: "flex",
          flexDirection: "column",
          rowGap: "30px",
          overflowY: "auto",
          padding: "48px 25px 12px",
        }}
      >
        {messages.map((message, index) => (
          <MessageItem key={index} message={message} sendToUser={sendToUser} />
        ))}
      </Box>
    </Box>
  );
};

export default MessageList;
